import { Suspense, useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import { BottomBar } from '@/widgets/bottom-bar';
import { Loader } from '@/widgets/loader';
import { useSuspenseQuery } from '@tanstack/react-query';
import { QK } from '@/shared/api/query-keys';
import UserService from '../api/service/user';
import { cloudStorage, miniApp, off, on, popup } from '@telegram-apps/sdk-react';
import { useUser } from '@/entities/user';
import i18n from '../i18n/i18n';
import { useTranslation } from 'react-i18next';
import EmployerService from '../api/service/employer';
import type { User } from '../api/model/user';
import type { Employer } from '../api/model/employer';
import type { Base } from '../api/model/base';

export const PageLayout = ({ bottomBar = true }: { bottomBar?: boolean }) => {
  const { setUser, setEmployer } = useUser();
  const { t } = useTranslation(['common', 'errors']);

  const { data } = useSuspenseQuery({
    queryKey: QK.init(),
    queryFn: async (): Promise<{ user: User; employer: Base<Employer> | null }> => {
      const user = await UserService.getMe();

      if (!user.accessToken) {
        return { user, employer: null };
      }

      await cloudStorage.setItem('accessToken', user.accessToken);

      const employer = await EmployerService.getMe();
      return { user, employer };
    },
  });

  useEffect(() => {
    const handlePopupClosed = () => miniApp.close();

    if (data.user.accessToken === null) {
      popup.show({
        title: t('error'),
        message: t('errors:signInError'),
        buttons: [{ id: 'close', type: 'destructive', text: t('close') }],
      });
      on('popup_closed', handlePopupClosed);
    } else {
      setUser(data.user);
      if (data.employer?.data) {
        setEmployer(data.employer.data);
      }
      i18n.changeLanguage(data.user.language.code);
      localStorage.setItem('languageId', String(data.user.language.id));
    }

    return () => {
      off('popup_closed', handlePopupClosed);
    };
  }, [data]);
  return (
    <div className="pb-4">
      <Suspense fallback={<Loader />}>
        <div>
          <Outlet />
        </div>
      </Suspense>
      {bottomBar && <BottomBar />}
    </div>
  );
};
